<script setup lang="ts">
	import { mountRohrsprung } from '~/utils/rohrsprung'

	const game = useTemplateRef<HTMLElement>('game')
	let destroy: (() => void) | undefined

	// Spiel erst im Browser starten (Canvas, window, localStorage gibt es nicht im SSR)
	onMounted(() => {
		destroy = mountRohrsprung(game.value)
	})

	// Listener, Animations-Loop und Observer wieder abbauen
	onBeforeUnmount(() => {
		destroy?.()
		destroy = undefined
	})
</script>

<template>
	<section class="lp404" aria-label="Minispiel">
		<div class="lp404__inner">

			<div ref="game" class="lp404__game" data-lp404-game>
				<div class="lp404__hud" aria-hidden="true">
					<span class="lp404__hud-hi" data-lp404-hi hidden>HI 00000</span>
					<span class="lp404__hud-score" data-lp404-score>00000</span>
				</div>

				<canvas class="lp404__canvas" data-lp404-canvas tabindex="0"
								role="img"
								aria-label="Optionales Minispiel: Ein Bauarbeiter springt über Rohre, Baken und weitere Baustellenobjekte und duckt sich unter Kranlasten hindurch."></canvas>

				<div class="lp404__overlay" data-lp404-overlay>
					<p class="lp404__overlay-title" data-lp404-overlay-title>Zeit für eine kurze Pause?</p>
					<p class="lp404__overlay-text" data-lp404-overlay-text>Leertaste, Klick oder Tippen zum Starten.</p>
					<button type="button" class="lp404__btn" data-lp404-start>Spiel starten</button>
				</div>
			</div>

		</div>
	</section>
</template>

<style>
/* ── Ludwig Pfeiffer 404 · alles unter .lp404 gekapselt ─────────────── */
.lp404 {
	--lp-navy: #003462;
	--lp-night: #002a4e;
	--lp-accent: #0abbef;
	--lp-muted: #62748e;
	--lp-bg: #fafbfc;
	padding: 0;
	color: var(--lp-navy);
	font-family: Quicksand, system-ui, sans-serif;
}

.lp404__inner { max-width: 60rem; margin-inline: auto; text-align: center; }

/* ── Spielfeld ─────────────────────────────────────────────────────── */
.lp404__game {
	position: relative;
	margin: 0 auto;
	max-width: 56rem;
	border: 1px solid color-mix(in srgb, var(--lp-navy) 14%, transparent);
	border-radius: 1rem;
	overflow: hidden;
	background: var(--lp-bg);
	transition: outline-color 0.2s ease;
}
@media (min-width: 640px) { .lp404__game { border-radius: 1.25rem; } }

/* Fokusring sitzt auf dem Rahmen, nicht auf dem Canvas.
	 So folgt er der Rundung und wird an den Ecken nicht abgeschnitten. */
.lp404__game:focus-within {
	outline: 2px solid color-mix(in srgb, var(--lp-accent) 70%, transparent);
	outline-offset: 3px;
}

.lp404__canvas {
	display: block;
	width: 100%;
	height: 300px;
	touch-action: none;
	outline: none;
	cursor: pointer;
}
@media (min-width: 640px) { .lp404__canvas { height: 350px; } }

.lp404__hud {
	position: absolute;
	top: 0.75rem; right: 1rem;
	z-index: 2;
	display: flex; gap: 0.85rem;
	font-variant-numeric: tabular-nums;
	font-weight: 700;
	font-size: 0.8rem;
	letter-spacing: 0.08em;
	color: color-mix(in srgb, var(--lp-navy) 55%, transparent);
	pointer-events: none;
	transition: color 0.4s ease;
}
.lp404__game[data-night] .lp404__hud { color: rgba(255,255,255,0.6); }
.lp404__hud-score { color: var(--lp-navy); }
.lp404__game[data-night] .lp404__hud-score { color: #fff; }
.lp404__hud-score[data-flash] { color: var(--lp-accent); }

.lp404__overlay {
	position: absolute; inset: 0;
	z-index: 3;
	display: flex; flex-direction: column; align-items: center; justify-content: center;
	gap: 0.4rem;
	padding: 1rem;
	text-align: center;
	background: color-mix(in srgb, var(--lp-bg) 82%, transparent);
	backdrop-filter: blur(2px);
}
.lp404__game[data-night] .lp404__overlay { background: color-mix(in srgb, var(--lp-night) 82%, transparent); }
.lp404__overlay[hidden] { display: none; }

.lp404__overlay-title {
	font-family: "Chillax Semibold", "Chillax Variable", Chillax, Quicksand, sans-serif;
	font-weight: 600;
	font-size: clamp(1.125rem, 3vw, 1.5rem);
	margin: 0;
	color: var(--lp-navy);
}
.lp404__overlay-text { margin: 0 0 0.6rem; font-size: 0.95rem; color: var(--lp-muted); }
.lp404__game[data-night] .lp404__overlay-title { color: #fff; }
.lp404__game[data-night] .lp404__overlay-text { color: rgba(255,255,255,0.7); }

.lp404__btn {
	font: inherit; font-weight: 700;
	border: 0; cursor: pointer;
	padding: 0.6rem 1.6rem;
	border-radius: 999px;
	background: var(--lp-accent);
	color: #fff;
	transition: filter 0.2s ease, transform 0.2s ease;
}
.lp404__btn:hover { filter: brightness(1.08); }
.lp404__btn:active { transform: translateY(1px); }
.lp404__btn:focus-visible { outline: 3px solid var(--lp-navy); outline-offset: 2px; }
.lp404__game[data-night] .lp404__btn:focus-visible { outline-color: #fff; }
</style>
